import { IconRefresh } from "@tabler/icons-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

interface AppErrorBoundaryProps {
  children: ReactNode;
  title?: string;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[AppErrorBoundary]", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    return (
      <div
        className="flex min-h-[40vh] w-full flex-col items-center justify-center gap-4 p-8 text-center"
        role="alert"
      >
        <div className="max-w-md space-y-2">
          <h2 className="text-lg font-medium text-foreground">
            {this.props.title ?? "Something went wrong"}
          </h2>
          <p className="text-sm text-pretty text-muted-foreground">
            {error.message || "An unexpected error occurred. Try again or reload the page."}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button type="button" variant="outline" onClick={this.handleReset}>
            <IconRefresh className="size-4" />
            Try again
          </Button>
          <Button type="button" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </div>
      </div>
    );
  }
}
